import { request } from './_core'

// ── Activities ────────────────────────────────────────────────

export interface ActivityRow {
  slug:         string
  requiredTier: 'BASIC' | 'PLUS' | 'PREMIUM'
  locked:       boolean
}

export interface ActivityItem {
  id:    number
  emoji: string
  es:    { word: string; definition: string }
  en:    { word: string; definition: string }
}

export type ThemeGenerationStatus = 'MANUAL' | 'IN_PROGRESS' | 'DRAFT' | 'FAILED'
export type ImageGenerationStatus = 'PENDING' | 'GENERATING' | 'DONE' | 'FAILED'

export interface ThemeSummary {
  id:               number
  slug:             string
  nameEs:           string
  nameEn:           string
  description:      string | null
  coverUrl:         string | null
  published:        boolean
  imageCount:       number
  generationStatus: ThemeGenerationStatus
  generationPrompt: string | null
  generationError:  string | null
  requiredTier:     'BASIC' | 'PLUS' | 'PREMIUM'
  locked:           boolean
  // Words that fit the wordsearch grid at each difficulty (>= 3 chars, <= grid size)
  wordsearchFit:    { es: { easy: number; medium: number; hard: number }; en: { easy: number; medium: number; hard: number } }
}

export interface ThemeImage {
  id:               number
  themeId:          number
  url:              string
  s3Key:            string
  altEs:            string
  altEn:            string
  wordEs:           string
  wordEn:           string
  definitionEs:     string | null
  definitionEn:     string | null
  value:            number | null
  sortOrder:        number
  generationStatus: ImageGenerationStatus
  generationError:  string | null
}

export interface ThemeDetail extends Omit<ThemeSummary, 'imageCount'> {
  images: ThemeImage[]
}

export interface ActivityDetail extends ActivityRow {
  data:       { items?: ActivityItem[] } | null
  dataSchema: Record<string, unknown>    | null
}

export interface AdminActivityRow {
  slug:              string
  requiredTier:      'BASIC' | 'PLUS' | 'PREMIUM'
  isPublished:       boolean
  sortOrder:         number
  category:          string | null
  usesGeneralThemes: boolean
  updatedAt:         string
}

export const activities = {
  list: () => request<ActivityRow[]>('/activities'),
  get:  (slug: string) => request<ActivityDetail>(`/activities/${slug}`),
}

export interface AdminActivityDetail extends AdminActivityRow {
  allowedThemeIds: number[]
  data:            { items?: ActivityItem[] } | null
  dataSchema:      Record<string, unknown>   | null
}

export const adminActivities = {
  list: () => request<AdminActivityRow[]>('/activities/admin/list'),
  get:  (slug: string) => request<AdminActivityDetail>(`/activities/admin/${slug}`),
  patch: (slug: string, body: {
    requiredTier?:      'BASIC' | 'PLUS' | 'PREMIUM'
    isPublished?:       boolean
    category?:          string | null
    usesGeneralThemes?: boolean
    allowedThemeIds?:   number[]
    data?:              Record<string, unknown>
  }) =>
    request<AdminActivityDetail>(`/activities/${slug}`, {
      method: 'PATCH',
      body:   JSON.stringify(body),
    }),
}
